/**
 * metricsEngine.js — Pure JS metrics computation. No React, no side-effects.
 */

import { parseMessage } from './gmailClient';
import { LOOP_CLOSURE_HOURS } from '../constants';

/**
 * Extract email address from a "Name <email>" string.
 * @param {string} raw
 * @returns {string}
 */
function extractEmail(raw = '') {
  const match = raw.match(/<([^>]+)>/);
  return match ? match[1].toLowerCase() : raw.toLowerCase().trim();
}

/**
 * Format a Date to 'YYYY-MM-DD'.
 * @param {Date} d
 * @returns {string}
 */
function toDateKey(d) {
  if (!d || !(d instanceof Date) || isNaN(d)) return null;
  return d.toISOString().split('T')[0];
}

/**
 * Process raw Gmail messages into a Metrics object.
 * @param {{ received: RawMessage[], sent: RawMessage[] }} raw
 * @returns {Metrics}
 */
export function processEmails({ received, sent }) {
  const parsedReceived = received.map((m) => parseMessage(m, 'received'));
  const parsedSent = sent.map((m) => parseMessage(m, 'sent'));
  const all = [...parsedReceived, ...parsedSent];

  // ── Volume by day ────────────────────────────────────────────────────────
  const volumeByDay = {};
  parsedReceived.forEach((m) => {
    const key = toDateKey(m.date);
    if (!key) return;
    if (!volumeByDay[key]) volumeByDay[key] = { date: key, received: 0, sent: 0 };
    volumeByDay[key].received++;
  });
  parsedSent.forEach((m) => {
    const key = toDateKey(m.date);
    if (!key) return;
    if (!volumeByDay[key]) volumeByDay[key] = { date: key, received: 0, sent: 0 };
    volumeByDay[key].sent++;
  });
  const volumeTrend = Object.values(volumeByDay).sort((a, b) => a.date.localeCompare(b.date));

  // ── Response times ───────────────────────────────────────────────────────
  // Map messageId → received message for thread matching
  const receivedByMessageId = {};
  parsedReceived.forEach((m) => {
    receivedByMessageId[m.messageId] = m;
  });

  const responseTimes = []; // minutes
  const responseDetails = []; // { from, email, subject, sentAt, responseMin }
  parsedSent.forEach((sent) => {
    if (!sent.inReplyTo) return;
    const original = receivedByMessageId[sent.inReplyTo];
    if (!original || !original.date || !sent.date) return;
    const diffMin = (sent.date.getTime() - original.date.getTime()) / 60000;
    if (diffMin > 0 && diffMin < 10080) { // cap at 1 week
      const rounded = Math.round(diffMin);
      responseTimes.push(rounded);
      responseDetails.push({
        from: original.from,
        email: extractEmail(original.from),
        subject: original.subject,
        body: original.body || original.snippet || '',
        receivedAt: original.date,
        responseMin: rounded,
      });
    }
  });

  const avgResponseTime = responseTimes.length
    ? Math.round(responseTimes.reduce((a, b) => a + b, 0) / responseTimes.length)
    : null;

  const sortedTimes = [...responseTimes].sort((a, b) => a - b);
  const medianResponseTime = sortedTimes.length
    ? sortedTimes[Math.floor(sortedTimes.length / 2)]
    : null;

  // Build histogram buckets (hours)
  const buckets = [
    { label: '<1h', max: 60, count: 0 },
    { label: '1–4h', max: 240, count: 0 },
    { label: '4–8h', max: 480, count: 0 },
    { label: '8–24h', max: 1440, count: 0 },
    { label: '>24h', max: Infinity, count: 0 },
  ];
  responseTimes.forEach((t) => {
    const bucket = buckets.find((b) => t <= b.max);
    if (bucket) bucket.count++;
  });
  const responseHistogram = buckets.map(({ label, count }) => ({ label, count }));

  // ── Hourly heatmap ───────────────────────────────────────────────────────
  const hourlyReceived = Array(24).fill(0);
  const hourlySent = Array(24).fill(0);
  parsedReceived.forEach((m) => {
    if (m.date) hourlyReceived[m.date.getHours()]++;
  });
  parsedSent.forEach((m) => {
    if (m.date) hourlySent[m.date.getHours()]++;
  });
  const hourlyHeatmap = Array.from({ length: 24 }, (_, h) => ({
    hour: h,
    received: hourlyReceived[h],
    sent: hourlySent[h],
    total: hourlyReceived[h] + hourlySent[h],
  }));

  // ── Email debt (received with no reply sent in thread) ───────────────────
  const repliedThreadIds = new Set(
    parsedSent.filter((m) => m.inReplyTo).map((m) => m.threadId)
  );
  const unanswered = parsedReceived.filter(
    (m) => m.threadId && !repliedThreadIds.has(m.threadId)
  );
  // Deduplicate by threadId — keep latest per thread
  const debtByThread = {};
  unanswered.forEach((m) => {
    if (!debtByThread[m.threadId] || m.date > debtByThread[m.threadId].date) {
      debtByThread[m.threadId] = m;
    }
  });
  const emailDebt = Object.values(debtByThread)
    .sort((a, b) => (b.date || 0) - (a.date || 0))
    .map((m) => ({
      from: m.from,
      email: extractEmail(m.from),
      subject: m.subject,
      body: m.body || m.snippet || '',
      receivedAt: m.date,
    }));

  // ── Day-of-week patterns ─────────────────────────────────────────────────
  const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const dowReceived = Array(7).fill(0);
  const dowSent = Array(7).fill(0);
  parsedReceived.forEach((m) => { if (m.date) dowReceived[m.date.getDay()]++; });
  parsedSent.forEach((m) => { if (m.date) dowSent[m.date.getDay()]++; });
  const dayOfWeekData = Array.from({ length: 7 }, (_, d) => ({
    day: DAYS[d],
    received: dowReceived[d],
    sent: dowSent[d],
    total: dowReceived[d] + dowSent[d],
  }));

  // ── Response rate by sender ───────────────────────────────────────────────
  // For each sender: how many did they send, how many did you reply to?
  const senderThreadsSent = new Map(); // email → Set of threadIds you replied to
  parsedSent.filter((m) => m.inReplyTo).forEach((m) => {
    // find the original received message to get sender email
    const original = receivedByMessageId[m.inReplyTo];
    if (!original) return;
    const email = extractEmail(original.from);
    if (!senderThreadsSent.has(email)) senderThreadsSent.set(email, new Set());
    senderThreadsSent.get(email).add(m.threadId);
  });

  const senderReceivedCount = {}; // email → total received
  const senderReceivedThreads = {}; // email → Set of threadIds received
  parsedReceived.forEach((m) => {
    const email = extractEmail(m.from);
    if (!email) return;
    senderReceivedCount[email] = (senderReceivedCount[email] || 0) + 1;
    if (!senderReceivedThreads[email]) senderReceivedThreads[email] = new Set();
    if (m.threadId) senderReceivedThreads[email].add(m.threadId);
  });

  const responseRateBySender = Object.entries(senderReceivedCount)
    .filter(([, count]) => count >= 3) // only senders with ≥3 emails (meaningful)
    .map(([email, totalReceived]) => {
      const repliedThreads = senderThreadsSent.get(email)?.size ?? 0;
      const totalThreads = senderReceivedThreads[email]?.size ?? totalReceived;
      const rate = totalThreads > 0 ? Math.round((repliedThreads / totalThreads) * 100) : 0;
      return { email, totalReceived, repliedThreads, totalThreads, rate };
    })
    .sort((a, b) => b.totalReceived - a.totalReceived)
    .slice(0, 15);

  // ── Top contacts ─────────────────────────────────────────────────────────
  const senderCounts = {};
  parsedReceived.forEach((m) => {
    const email = extractEmail(m.from);
    if (!email) return;
    senderCounts[email] = (senderCounts[email] || 0) + 1;
  });

  const recipientCounts = {};
  parsedSent.forEach((m) => {
    const emails = m.to.split(',').map((e) => extractEmail(e)).filter(Boolean);
    emails.forEach((email) => {
      recipientCounts[email] = (recipientCounts[email] || 0) + 1;
    });
  });

  const topSenders = Object.entries(senderCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 10)
    .map(([email, count]) => ({ email, count }));

  const topRecipients = Object.entries(recipientCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 10)
    .map(([email, count]) => ({ email, count }));

  // ── Communication loops (pending & closed) ───────────────────────────────
  // Build per-thread state: who sent last, timestamps, participation from both sides
  const threadMap = {}; // threadId → { lastMsg, lastDirection, userParticipated, otherParticipated, subject, contact, contactEmail, firstDate, lastDate }
  const userSentThreads = new Set();
  const userReceivedThreads = new Set();

  parsedReceived.forEach((m) => {
    if (!m.threadId) return;
    userReceivedThreads.add(m.threadId);
    const existing = threadMap[m.threadId];
    if (!existing || m.date > existing.lastDate) {
      threadMap[m.threadId] = {
        ...(existing || {}),
        lastDate: m.date,
        lastDirection: 'received',
        lastMessageId: m.id,
        subject: m.subject,
        lastBody: m.body || m.snippet || '',
        contact: m.from,
        contactEmail: extractEmail(m.from),
        firstDate: existing ? (m.date < existing.firstDate ? m.date : existing.firstDate) : m.date,
        otherParticipated: true,
        userParticipated: existing?.userParticipated || false,
      };
    } else {
      if (!existing.firstDate || m.date < existing.firstDate) existing.firstDate = m.date;
      existing.otherParticipated = true;
    }
  });

  parsedSent.forEach((m) => {
    if (!m.threadId) return;
    userSentThreads.add(m.threadId);
    const existing = threadMap[m.threadId];
    // For sent messages, the contact is the recipient
    const contactRaw = m.to.split(',')[0] || '';
    const contactEmail = extractEmail(contactRaw);
    if (!existing || m.date > existing.lastDate) {
      threadMap[m.threadId] = {
        ...(existing || {}),
        lastDate: m.date,
        lastDirection: 'sent',
        lastMessageId: m.id,
        subject: existing?.subject || m.subject,
        lastBody: m.body || m.snippet || existing?.lastBody || '',
        contact: existing?.contact || contactRaw,
        contactEmail: existing?.contactEmail || contactEmail,
        firstDate: existing ? (m.date < existing.firstDate ? m.date : existing.firstDate) : m.date,
        userParticipated: true,
        otherParticipated: existing?.otherParticipated || false,
      };
    } else {
      if (!existing.firstDate || m.date < existing.firstDate) existing.firstDate = m.date;
      existing.userParticipated = true;
    }
  });

  const now = new Date();
  const closureMs = LOOP_CLOSURE_HOURS * 3600000;
  const pendingLoops = [];
  const closedLoops = [];

  Object.entries(threadMap).forEach(([threadId, t]) => {
    if (!t.lastDate) return;
    const ageMs = now.getTime() - t.lastDate.getTime();
    const ageHours = Math.round(ageMs / 3600000);
    const durationMs = t.lastDate.getTime() - (t.firstDate || t.lastDate).getTime();
    const durationHours = Math.round(durationMs / 3600000);
    const bothParticipated = t.userParticipated && t.otherParticipated;

    const entry = {
      threadId,
      contact: t.contact,
      contactEmail: t.contactEmail,
      subject: t.subject,
      lastBody: t.lastBody,
      lastDate: t.lastDate,
      firstDate: t.firstDate,
      lastDirection: t.lastDirection,
      lastMessageId: t.lastMessageId,
      ageHours,
      durationHours,
    };

    if (bothParticipated && ageMs >= closureMs) {
      // Both sides participated and thread went quiet → closed
      closedLoops.push({ ...entry, closedAt: t.lastDate });
    } else if (!bothParticipated || ageMs < closureMs) {
      // Still waiting for a response from one side
      if (t.lastDirection === 'sent' && !t.otherParticipated) {
        // You sent, they never replied — awaiting their reply
        pendingLoops.push({ ...entry, awaitingDirection: 'theirs' });
      } else if (t.lastDirection === 'received' && !t.userParticipated) {
        // They sent, you never replied — awaiting your reply
        pendingLoops.push({ ...entry, awaitingDirection: 'yours' });
      } else if (bothParticipated && ageMs < closureMs) {
        // Both participated but conversation is still fresh
        if (t.lastDirection === 'received') {
          pendingLoops.push({ ...entry, awaitingDirection: 'yours' });
        } else {
          pendingLoops.push({ ...entry, awaitingDirection: 'theirs' });
        }
      }
    }
  });

  // Sort: pending by age descending (oldest first), closed by closedAt descending
  pendingLoops.sort((a, b) => b.ageHours - a.ageHours);
  closedLoops.sort((a, b) => (b.closedAt || 0) - (a.closedAt || 0));

  // Compute average loop closure time from closed loops that had both sides
  const closureDurations = closedLoops.filter((l) => l.durationHours > 0).map((l) => l.durationHours);
  const avgLoopClosureHours = closureDurations.length
    ? Math.round(closureDurations.reduce((a, b) => a + b, 0) / closureDurations.length)
    : null;

  // ── Summary stats ────────────────────────────────────────────────────────
  return {
    totalReceived: parsedReceived.length,
    totalSent: parsedSent.length,
    volumeTrend,
    avgResponseTime,
    medianResponseTime,
    responseHistogram,
    responseTimes,
    responseDetails,
    hourlyHeatmap,
    dayOfWeekData,
    emailDebt,
    topSenders,
    topRecipients,
    responseRateBySender,
    pendingLoops,
    closedLoops,
    avgLoopClosureHours,
    hasReplies: responseTimes.length > 0,
  };
}
